import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getStudentStatus, getImageUrl, updateStudentProfile } from '../api/api';
import '../styles/student.css';
import '../styles/student-dashboard.css';

// RGIPT Logo - place logo at public/rgipt-logo.png
const RGIPT_LOGO = '/rgipt-logo.png';

// Hostel options — same values/labels as the signup form
const HOSTEL_OPTIONS = [
  { value: 'Sarojini', label: 'Sarojini (A)' },
  { value: 'Asima Chatterjee', label: 'Asima Chatterjee (Urja)' },
  { value: 'Visvesvaraya', label: 'Visvesvaraya (B)' },
  { value: 'CV Raman', label: 'CV Raman (C)' },
  { value: 'Ramanujan', label: 'Ramanujan (D)' },
  { value: 'Aryabhatta', label: 'Aryabhatta (E)' },
  // Display name only — the stored value must stay 'F-Block' to keep matching
  // the warden account and existing student records.
  { value: 'F-Block', label: 'Subhas Chandra Bose (F)' },
  { value: 'Vidyasagar', label: 'Vidyasagar (G)' },
  { value: 'Homi Bhabha', label: 'Homi Bhabha (H)' },
  { value: 'RGIPT housing', label: 'RGIPT housing (Married Accommodation)' },
];

// Stored hostel value -> display label. Purely cosmetic: the stored value is
// never changed, so warden scoping and gatepass routing are unaffected. Falls
// back to the stored string for any value not in the list.
const getHostelLabel = (storedHostel) => {
    if (!storedHostel) return '';
    const exact = HOSTEL_OPTIONS.find((hostel) => hostel.value === storedHostel);
    if (exact) return exact.label;
    const baseName = String(storedHostel).replace(/\s*\([^)]*\)\s*$/, '').trim().toLowerCase();
    const loose = HOSTEL_OPTIONS.find((hostel) => hostel.value.toLowerCase() === baseName);
    return loose ? loose.label : storedHostel;
};

// Professional SVG Icons
const Icons = {
  camera: <svg className="sp-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"></path><circle cx="12" cy="13" r="4"></circle></svg>,
};

const StudentProfile = () => {
    const navigate = useNavigate();
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(true);

    // Edit profile — room number and hostel are the only fields a student may change
    const [showEditModal, setShowEditModal] = useState(false);
    const [roomInput, setRoomInput] = useState('');
    const [hostelInput, setHostelInput] = useState('');
    const [saving, setSaving] = useState(false);
    const [editError, setEditError] = useState('');
    const [editSuccess, setEditSuccess] = useState('');

    useEffect(() => {
        const fetchStatus = async () => {
            try {
                const res = await getStudentStatus();
                setStatus(res.data);
            } catch (err) {
                console.error('Failed to fetch status:', err);
            } finally {
                setLoading(false);
            }
        };
        fetchStatus();
    }, []);

    // Auto-hide the "saved" confirmation
    useEffect(() => {
        if (!editSuccess) return undefined;
        const timer = setTimeout(() => setEditSuccess(''), 4000);
        return () => clearTimeout(timer);
    }, [editSuccess]);

    // Calculate batch from roll number (e.g., 23cd3037 → 2023-27)
    const calculateBatch = (rollNumber) => {
        if (!rollNumber || rollNumber.length < 2) return '--';
        const prefix = rollNumber.substring(0, 2);
        const startYear = parseInt(prefix, 10);
        if (isNaN(startYear)) return '--';
        const fullStartYear = 2000 + startYear;
        const endYear = startYear + 4;
        return `${fullStartYear}-${endYear}`;
    };

    const normalizeBranch = (branch) => {
        if (!branch) return '--';
        return branch;
    };

    // Same rule the backend enforces (e.g. E-501, B 204, C/12)
    const ROOM_NUMBER_PATTERN = /^[A-Za-z0-9][A-Za-z0-9\-/ ]*$/;

    // Map a stored hostel name onto a dropdown option. Handles the labelled form
    // too (e.g. "Vidyasagar (G)" -> "Vidyasagar") so the current hostel preselects.
    const matchHostelOption = (storedHostel) => {
        if (!storedHostel) return '';
        const exact = HOSTEL_OPTIONS.find((hostel) => hostel.value === storedHostel);
        if (exact) return exact.value;
        const baseName = String(storedHostel).replace(/\s*\([^)]*\)\s*$/, '').trim().toLowerCase();
        const loose = HOSTEL_OPTIONS.find((hostel) => hostel.value.toLowerCase() === baseName);
        return loose ? loose.value : '';
    };

    const openEditModal = () => {
        setRoomInput(status?.roomNumber || '');
        setHostelInput(matchHostelOption(status?.hostelName));
        setEditError('');
        setEditSuccess('');
        setShowEditModal(true);
    };

    const closeEditModal = () => {
        if (saving) return;
        setShowEditModal(false);
        setEditError('');
    };

    const handleSaveProfile = async (e) => {
        e.preventDefault();
        const trimmedRoom = roomInput.trim();
        const selectedHostel = hostelInput.trim();

        if (!trimmedRoom) {
            setEditError('Room number cannot be empty');
            return;
        }
        if (trimmedRoom.length > 20) {
            setEditError('Room number must be 20 characters or less');
            return;
        }
        if (!ROOM_NUMBER_PATTERN.test(trimmedRoom)) {
            setEditError('Room number can only contain letters, numbers, spaces, - and /');
            return;
        }
        if (!selectedHostel) {
            setEditError('Please select your hostel');
            return;
        }
        if (!HOSTEL_OPTIONS.some((hostel) => hostel.value === selectedHostel)) {
            setEditError('Invalid hostel selected');
            return;
        }

        const roomChanged = trimmedRoom !== (status?.roomNumber || '');
        const hostelChanged = selectedHostel !== (status?.hostelName || '');
        if (!roomChanged && !hostelChanged) {
            setEditError('Nothing to update — these are already your current details');
            return;
        }

        setSaving(true);
        setEditError('');
        try {
            const res = await updateStudentProfile({ roomNumber: trimmedRoom, hostelName: selectedHostel });
            const updatedRoomNumber = res?.data?.roomNumber || trimmedRoom;
            const updatedHostelName = res?.data?.hostelName || selectedHostel;
            setStatus((prev) => (prev ? { ...prev, roomNumber: updatedRoomNumber, hostelName: updatedHostelName } : prev));
            setShowEditModal(false);
            setEditSuccess('Profile updated successfully');
        } catch (err) {
            setEditError(err?.response?.data?.message || 'Failed to update profile. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="sd-shell">
                <header className="sd-header">
                    <div className="sd-header-brand">
                        <span className="sd-logo">GoThru</span>
                    </div>
                    <button className="sp-back-btn" onClick={() => navigate('/student')}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M19 12H5M12 19l-7-7 7-7"/>
                        </svg>
                        Back
                    </button>
                </header>
                <main className="sd-main">
                    <div style={{ display: 'flex', justifyContent: 'center', padding: '40px', color: 'var(--sd-muted)' }}>
                        <div className="loader" role="status" aria-label="Loading"></div>
                    </div>
                </main>
            </div>
        );
    }

    const studentName = status?.studentName || '--';
    const rollNumber = (status?.rollnumber || '--').toUpperCase();
    const course = status?.course || 'B.Tech';
    const branch = normalizeBranch(status?.branch);
    const department = status?.department || '--';
    const batch = calculateBatch(status?.rollnumber);
    const roomNumber = status?.roomNumber || '--';
    const hostelName = getHostelLabel(status?.hostelName) || '--';
    const contactNumber = status?.contactNumber || '--';
    const imageUrl = getImageUrl(status?.imageUrl);
    // Bangalore-campus students get read-only access in this Jais deployment
    const isReadOnly = status?.campus === 'bangalore';

    return (
        <div className="sd-shell">
            <header className="sd-header">
                <div className="sd-header-brand">
                    <span className="sd-logo">GoThru</span>
                </div>
                <button className="sp-back-btn" onClick={() => navigate('/student')}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M19 12H5M12 19l-7-7 7-7"/>
                    </svg>
                    Back
                </button>
            </header>

            <main className="sd-main" style={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', paddingTop: '24px' }}>
                <div className="sp-profile-stack">
                {/* ID Card Container */}
                <div className="id-card">
                    {/* Header Section with College Name */}
                    <div className="id-card-header">
                        {/* Left Side - Logo */}
                        <div className="id-card-logo-section">
                            <img
                                src={RGIPT_LOGO}
                                alt="RGIPT Logo"
                                className="id-card-logo"
                                onError={(e) => { e.target.style.display = 'none'; }}
                            />
                            <div className="id-card-logo-text">GoThru x RGIPT</div>
                        </div>

                        {/* Right Side - College Name */}
                        <div className="id-card-college-info">
                            <div className="id-card-college-name-en">RAJIV GANDHI INSTITUTE OF PETROLEUM TECHNOLOGY</div>
                            <div className="id-card-college-subtitle">(An Institute of National Importance Established Under an Act of Parliament)</div>
                            <div className="id-card-college-name-hi">राजीव गाँधी पेट्रोलियम प्रौद्योगिकी संस्थान</div>
                            <div className="id-card-college-subtitle-hi">(संसद के अधिनियम द्वारा राष्ट्रीय महत्व का संस्थान)</div>
                            <div className="id-card-address">Mubarakpur Mukhetia More, Bahadurpur, Post: Harbanshganj, Jais, Amethi-229304, (U.P.) India</div>
                        </div>
                    </div>

                    {/* Tricolor Line */}
                    <div className="id-card-tricolor">
                        <div className="tricolor-saffron"></div>
                        <div className="tricolor-white"></div>
                        <div className="tricolor-green"></div>
                    </div>

                    {/* Card Title */}
                    <div className="id-card-title">Student GoThru Pass Card</div>

                    {/* Main Content */}
                    <div className="id-card-body">
                        {/* Photo Section */}
                        <div className="id-card-photo-section">
                            <div className="id-card-photo-frame">
                                {imageUrl ? (
                                    <img 
                                        src={imageUrl} 
                                        alt="Student" 
                                        className="id-card-photo"
                                        onError={(e) => {
                                            e.target.onerror = null;
                                            e.target.style.display = 'none';
                                            e.target.parentElement.innerHTML = '<div class="id-card-photo-placeholder"><span class="sp-camera-icon"></span></div>';
                                        }}
                                    />
                                ) : (
                                    <div className="id-card-photo-placeholder">
                                        {Icons.camera}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Details Section */}
                        <div className="id-card-details">
                            <div className="id-card-row">
                                <span className="id-card-label">Name</span>
                                <span className="id-card-colon">:</span>
                                <span className="id-card-value">{studentName}</span>
                            </div>
                            <div className="id-card-row">
                                <span className="id-card-label">Room & Hostel</span>
                                <span className="id-card-colon">:</span>
                                <span className="id-card-value">{roomNumber}, {hostelName}</span>
                            </div>
                            <div className="id-card-row">
                                <span className="id-card-label">Contact No.</span>
                                <span className="id-card-colon">:</span>
                                <span className="id-card-value">{contactNumber}</span>
                            </div>
                            <div className="id-card-row">
                                <span className="id-card-label">Roll No.</span>
                                <span className="id-card-colon">:</span>
                                <span className="id-card-value">{rollNumber}</span>
                            </div>
                            <div className="id-card-row">
                                <span className="id-card-label">Course</span>
                                <span className="id-card-colon">:</span>
                                <span className="id-card-value">{course}</span>
                            </div>
                            <div className="id-card-row">
                                <span className="id-card-label">Branch</span>
                                <span className="id-card-colon">:</span>
                                <span className="id-card-value">{branch}</span>
                            </div>
                            <div className="id-card-row">
                                <span className="id-card-label">Department</span>
                                <span className="id-card-colon">:</span>
                                <span className="id-card-value">{department}</span>
                            </div>
                            <div className="id-card-row">
                                <span className="id-card-label">Batch</span>
                                <span className="id-card-colon">:</span>
                                <span className="id-card-value">{batch}</span>
                            </div>
                        </div>
                    </div>


                </div>

                {/* Edit Profile — room number only */}
                {editSuccess && (
                    <div className="sp-edit-success" role="status">{editSuccess}</div>
                )}

                <button
                    className="sp-edit-btn"
                    onClick={openEditModal}
                    disabled={isReadOnly}
                    title={isReadOnly ? 'Read-only — you are on the Bangalore campus' : 'Edit your hostel and room number'}
                >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 20h9" />
                        <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4z" />
                    </svg>
                    Edit Profile
                </button>

                <p className="sp-edit-note">
                    {isReadOnly
                        ? 'Read-only — you are on the Bangalore campus.'
                        : 'Only your hostel and room number can be changed. For any other correction, contact the hostel office.'}
                </p>
                </div>
            </main>

            {/* Edit Room Number Modal */}
            {showEditModal && (
                <div className="sd-modal-overlay" onClick={closeEditModal}>
                    <div className="sd-modal" onClick={(e) => e.stopPropagation()}>
                        <button className="sd-modal-close" onClick={closeEditModal} aria-label="Close">×</button>
                        <h3 className="sd-modal-title">Edit Profile</h3>

                        <form onSubmit={handleSaveProfile}>
                            <div className="sp-edit-field">
                                <label className="sp-edit-label" htmlFor="sp-hostel-name">Hostel</label>
                                <select
                                    id="sp-hostel-name"
                                    className="sp-edit-input sp-edit-select"
                                    value={hostelInput}
                                    onChange={(e) => setHostelInput(e.target.value)}
                                    disabled={saving}
                                    required
                                >
                                    <option value="" disabled>Select hostel</option>
                                    {HOSTEL_OPTIONS.map((hostel) => (
                                        <option key={hostel.value} value={hostel.value}>
                                            {hostel.label}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="sp-edit-field">
                                <label className="sp-edit-label" htmlFor="sp-room-number">Room Number</label>
                                <input
                                    id="sp-room-number"
                                    type="text"
                                    className="sp-edit-input"
                                    value={roomInput}
                                    onChange={(e) => setRoomInput(e.target.value)}
                                    placeholder="e.g. E-501"
                                    maxLength={20}
                                    autoFocus
                                    disabled={saving}
                                />
                            </div>

                            <div className="sp-edit-readonly">
                                <div className="sp-edit-readonly-row">
                                    <span>Roll No.</span>
                                    <strong>{rollNumber}</strong>
                                </div>
                            </div>

                            {editError && <div className="sp-edit-error">{editError}</div>}

                            <div className="sp-edit-actions">
                                <button
                                    type="button"
                                    className="sp-edit-cancel"
                                    onClick={closeEditModal}
                                    disabled={saving}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="sp-edit-save"
                                    disabled={saving}
                                >
                                    {saving ? 'Saving...' : 'Save Changes'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default StudentProfile;
